import React, { useState, useMemo, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import { useUserRole } from "../../../context/UserRoleContext";
import { useBar } from "../../../context/BarContext";
import DashboardLayout from "../../../components/common/DashboardLayout";
import MessageBell from "../../../components/common/MessageBell";
import NotificationBell from "../../../components/common/NotificationBell";
import useBarArtists from "../hooks/useBarArtists";
import { getEffectiveSlotsForDate } from "../../../../lib/barTimeSlots";
import ShowCardModal from "../components/ShowCardModal";
import AddShowModal from "../components/AddShowModal";
import {
  calculateSalesTotal,
  calculateBasePayTotal,
  formatCurrency,
} from "../../../utils/financialMath";
import {
  filterShowsByDateAndTime,
  getCurrentDateTime,
} from "../utils/financialsMath";
import { formatDateForInput } from "../../../utils/calendarDate";
import {
  civilDateTodayInZone,
  normalizeBarTimeZone,
} from "../../../utils/civilDateTime";
import { DateTime } from "luxon";

const BarOwnerDashboard = () => {
  const { currentUser } = useAuth();
  const { userData, loading, isBarAdmin } = useUserRole();
  const {
    selectedBar,
    userBars,
    loading: barsLoading,
    barShows,
    showsLoading,
    refreshBarShows,
  } = useBar();
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [isAddShowModalOpen, setIsAddShowModalOpen] = useState(false);
  const [addShowDefaults, setAddShowDefaults] = useState(() => ({
    date: civilDateTodayInZone(normalizeBarTimeZone(undefined)),
    timeSlotId: "",
    artistId: "",
  }));
  const [isShowModalOpen, setIsShowModalOpen] = useState(false);
  const [selectedShow, setSelectedShow] = useState(null);

  // Calculate date ranges for the week view
  const weekDates = useMemo(() => {
    const dates = [];
    const today = new Date(currentDate);
    for (let i = -1; i < 6; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [currentDate]);

  const startDate = formatDateForInput(weekDates[0]);
  const endDate = formatDateForInput(weekDates[weekDates.length - 1]);

  const normalizedShows = useMemo(() => {
    if (!barShows?.length) return [];
    return barShows.map((show) => ({
      ...show,
      date: formatDateForInput(show.date),
    }));
  }, [barShows]);

  const showsForWeek = useMemo(() => {
    if (!normalizedShows.length) return [];
    return normalizedShows.filter(
      (show) => show.date >= startDate && show.date <= endDate
    );
  }, [normalizedShows, startDate, endDate]);

  useEffect(() => {
    if (selectedBar?.bar_id) {
      console.log("Loaded bar shows:", {
        barId: selectedBar.bar_id,
        totalShows: barShows.length,
        shows: barShows,
      });
    } else {
      console.log("No bar selected. Shows cleared.");
    }
  }, [selectedBar?.bar_id, barShows]);

  const { artists, loading: artistsLoading } = useBarArtists(
    selectedBar?.bar_id
  );

  const getSlotsForDate = (date) => {
    if (!selectedBar?.time_slots) return [];
    const ymd = formatDateForInput(date);
    return getEffectiveSlotsForDate(selectedBar.time_slots, ymd);
  };

  // Calculate financial data from barShows (venue-local civil dates + wall times)
  const financialData = useMemo(() => {
    const tz = normalizeBarTimeZone(selectedBar?.time_zone);
    if (!barShows || barShows.length === 0) {
      return {
        yesterday: { basePay: 0, sales: 0, profit: 0 },
        last7Days: { basePay: 0, sales: 0, profit: 0 },
        last30Days: { basePay: 0, sales: 0, profit: 0 },
        yearToDate: { basePay: 0, sales: 0, profit: 0 },
      };
    }

    const currentDateTime = getCurrentDateTime();
    const todayVenue = DateTime.now().setZone(tz).startOf("day");
    const yesterdayYmd = todayVenue.minus({ days: 1 }).toISODate();
    const last7StartYmd = todayVenue.minus({ days: 6 }).toISODate();
    const todayYmd = todayVenue.toISODate();
    const last30StartYmd = todayVenue.minus({ days: 29 }).toISODate();
    const yearStartYmd = todayVenue.startOf("year").toISODate();

    const yesterdayShows = filterShowsByDateAndTime(
      barShows,
      yesterdayYmd,
      yesterdayYmd,
      currentDateTime,
      tz,
    );
    const last7DaysShows = filterShowsByDateAndTime(
      barShows,
      last7StartYmd,
      todayYmd,
      currentDateTime,
      tz,
    );
    const last30DaysShows = filterShowsByDateAndTime(
      barShows,
      last30StartYmd,
      todayYmd,
      currentDateTime,
      tz,
    );
    const yearToDateShows = filterShowsByDateAndTime(
      barShows,
      yearStartYmd,
      todayYmd,
      currentDateTime,
      tz,
    );

    // Calculate totals for each period
    const calculatePeriodTotals = (shows) => {
      const basePay = calculateBasePayTotal(shows);
      const sales = calculateSalesTotal(shows);
      const profit = sales - basePay; // Profit = Sales - Base Pay (expense)
      return { basePay, sales, profit };
    };

    return {
      yesterday: calculatePeriodTotals(yesterdayShows),
      last7Days: calculatePeriodTotals(last7DaysShows),
      last30Days: calculatePeriodTotals(last30DaysShows),
      yearToDate: calculatePeriodTotals(yearToDateShows),
    };
  }, [barShows, selectedBar?.time_zone]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (!selectedBar) {
    if (barsLoading) {
      return (
        <DashboardLayout>
          <div className="loading-container">
            <div className="loading-spinner">Loading...</div>
          </div>
        </DashboardLayout>
      );
    }

    if (isBarAdmin && userBars.length === 0) {
      const inviteEmail =
        currentUser?.email || userData?.email || "your account email";
      return (
        <DashboardLayout>
          <div className="bar-owner-dashboard">
            <div className="bar-admin-dashboard-empty">
              <h2>You currently don&apos;t belong to any bars.</h2>
              <p>
                Message bar owners to send you an invite to this email:
              </p>
              <p className="bar-admin-dashboard-email">{inviteEmail}</p>
            </div>
          </div>
        </DashboardLayout>
      );
    }

    return (
      <DashboardLayout>
        <div className="bar-owner-dashboard">
          <div className="no-bar-message">
            <h2>No Bar Selected</h2>
            <p>
              Please select a bar from the dropdown or add a new bar in
              Management.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const formatTime = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "pm" : "am";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes}${ampm}`;
  };

  const parseTimeToMinutes = (time) => {
    if (!time) return null;
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours, 10);
    const minute = parseInt(minutes || "0", 10);
    if (Number.isNaN(hour) || Number.isNaN(minute)) {
      return null;
    }
    return hour * 60 + minute;
  };

  const doesShowMatchSlotExactly = (show, slot) => {
    return (
      parseTimeToMinutes(show.start_time) ===
        parseTimeToMinutes(slot.start_time) &&
      parseTimeToMinutes(show.end_time) === parseTimeToMinutes(slot.end_time)
    );
  };

  // Helper to get shows for a specific date and time slot
  const getExactShowForSlot = (date, slot, showsForDay) => {
    return showsForDay.find((show) => doesShowMatchSlotExactly(show, slot));
  };

  const getShowsForDay = (date) => {
    const dateStr = formatDateForInput(date);
    return showsForWeek
      .filter((show) => show.date === dateStr)
      .sort((a, b) => {
        const aStart = parseTimeToMinutes(a.start_time) ?? 0;
        const bStart = parseTimeToMinutes(b.start_time) ?? 0;
        return aStart - bStart;
      });
  };

  const handleSlotClick = (show, slot, date) => {
    const dateStr = formatDateForInput(date);
    if (!show) {
      openAddShowModal(dateStr, slot?.id ?? "");
      return;
    }

    setSelectedShow({
      ...show,
      date: show.date || dateStr,
    });
    setIsShowModalOpen(true);
  };

  // Calendar navigation functions
  const moveDayBackward = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  const moveDayForward = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  const moveWeekBackward = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const moveWeekForward = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const isToday = (date) => {
    const todayDate = new Date();
    return (
      date.getDate() === todayDate.getDate() &&
      date.getMonth() === todayDate.getMonth() &&
      date.getFullYear() === todayDate.getFullYear()
    );
  };

  // Get month display for the week
  const getMonthDisplay = () => {
    const firstDate = weekDates[0];
    const lastDate = weekDates[weekDates.length - 1];
    const firstMonth = firstDate.toLocaleDateString("en-US", {
      month: "short",
    });
    const lastMonth = lastDate.toLocaleDateString("en-US", { month: "short" });

    if (firstMonth === lastMonth) {
      return firstMonth;
    }
    return `${firstMonth} - ${lastMonth}`;
  };

  const openAddShowModal = (
    date = civilDateTodayInZone(
      normalizeBarTimeZone(selectedBar?.time_zone)
    ),
    timeSlotId = "",
    artistId = ""
  ) => {
    setAddShowDefaults({
      date: formatDateForInput(date),
      timeSlotId: timeSlotId ? String(timeSlotId) : "",
      artistId: artistId ? String(artistId) : "",
    });
    setIsAddShowModalOpen(true);
  };

  const closeAddShowModal = () => {
    setIsAddShowModalOpen(false);
    setAddShowDefaults({
      date: civilDateTodayInZone(
        normalizeBarTimeZone(selectedBar?.time_zone)
      ),
      timeSlotId: "",
      artistId: "",
    });
  };

  const closeShowCardModal = () => {
    setIsShowModalOpen(false);
    setSelectedShow(null);
  };

  return (
    <DashboardLayout>
      <div className="bar-owner-dashboard">
        {/* Calendar Section */}
        <div
          className="calendar-section"
          aria-busy={showsLoading ? "true" : "false"}
        >
          {showsLoading && (
            <div className="calendar-loading-overlay" role="status">
              <div className="calendar-loading-indicator">
                <span className="calendar-loading-spinner" aria-hidden="true" />
                <span className="calendar-loading-text">
                  Refreshing schedule…
                </span>
              </div>
            </div>
          )}
          {/* Calendar Navigation Buttons */}
          <div className="calendar-nav">
            <button
              type="button"
              className="nav-btn add-show-btn"
              onClick={() => openAddShowModal()}
            >
              + Add Show
            </button>
            <div className="calendar-nav-content">
              <div className="nav-btn-group">
                <button className="nav-btn week-btn" onClick={moveWeekBackward}>
                  ← Week
                </button>
                <button className="nav-btn day-btn" onClick={moveDayBackward}>
                  ← Day
                </button>
              </div>
              <div className="calendar-month-display">{getMonthDisplay()}</div>
              <div className="nav-btn-group">
                <button className="nav-btn day-btn" onClick={moveDayForward}>
                  Day →
                </button>
                <button className="nav-btn week-btn" onClick={moveWeekForward}>
                  Week →
                </button>
              </div>
            </div>
            <div className="calendar-nav-right">
              <MessageBell />
              <NotificationBell linkOnly />
            </div>
          </div>

          {/* Week Calendar */}
          <div className="week-calendar">
            {weekDates.map((date, index) => (
              <div
                key={index}
                className={`calendar-day ${isToday(date) ? "today" : ""}`}
              >
                <div className="day-header">
                  <div className="day-name">
                    {date.toLocaleDateString("en-US", { weekday: "short" })}
                  </div>
                  <div className="day-number">{date.getDate()}</div>
                </div>

                <div className="day-shows">
                  {(() => {
                    const showsForDay = getShowsForDay(date);
                    const matchedShowIds = new Set();
                    const timeSlots = getSlotsForDate(date);

                    const slotCards = timeSlots.map((slot) => {
                      const show = getExactShowForSlot(date, slot, showsForDay);
                      if (show) {
                        const showKey =
                          show.show_id ??
                          `${show.date}-${show.start_time}-${
                            show.artist_id ?? ""
                          }`;
                        matchedShowIds.add(showKey);
                      }

                      return (
                        <div
                          key={`slot-${slot.id}-${formatDateForInput(date)}`}
                          className={`show-slot ${slot.name.toLowerCase()} ${
                            show ? "has-show" : "no-show"
                          }`}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleSlotClick(show, slot, date)}
                          onKeyPress={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              handleSlotClick(show, slot, date);
                            }
                          }}
                        >
                          <div className="slot-header">
                            <span className="slot-title">{slot.name}</span>
                            <span className="slot-subtitle">
                              {formatTime(slot.start_time)} -{" "}
                              {formatTime(slot.end_time)}
                            </span>
                          </div>
                          {show ? (
                            <div
                              className={`slot-content booked ${
                                show.status === "pending" ? "pending" : ""
                              }`}
                            >
                              <span className="artist-name">
                                {show.stage_name || "Artist"}
                              </span>
                              {show.status === "pending" && (
                                <span className="pending-badge">
                                  ⏳ Pending
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="slot-content empty">
                              <span className="no-show-text">
                                No show added
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    });

                    const offSlotShows = showsForDay.filter((show) => {
                      const showKey =
                        show.show_id ??
                        `${show.date}-${show.start_time}-${
                          show.artist_id ?? ""
                        }`;
                      return !matchedShowIds.has(showKey);
                    });

                    return (
                      <>
                        {slotCards}
                        {offSlotShows.map((show) => {
                          const showKey =
                            show.show_id ??
                            `${show.date}-${show.start_time}-${
                              show.artist_id ?? ""
                            }`;
                          return (
                            <div
                              key={`offslot-${showKey}`}
                              className="show-slot has-show"
                              role="button"
                              tabIndex={0}
                              onClick={() => handleSlotClick(show, null, date)}
                              onKeyPress={(event) => {
                                if (
                                  event.key === "Enter" ||
                                  event.key === " "
                                ) {
                                  handleSlotClick(show, null, date);
                                }
                              }}
                            >
                              <div className="slot-header">
                                <span className="slot-title">Custom times</span>
                                <span className="slot-subtitle">
                                  {formatTime(show.start_time)} -{" "}
                                  {formatTime(show.end_time)}
                                </span>
                              </div>
                              <div
                                className={`slot-content booked ${
                                  show.status === "pending" ? "pending" : ""
                                }`}
                              >
                                <span className="artist-name">
                                  {show.stage_name || "Artist"}
                                </span>
                                {show.status === "pending" && (
                                  <span className="pending-badge">
                                    ⏳ Pending
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Financial Section */}
        <div className="financial-section">
          <h2 className="section-title">Financial Overview</h2>
          {showsLoading ? (
            <div className="financial-grid">
              <div className="financial-card">
                <h3>Yesterday's Sales</h3>
                <div className="earnings-breakdown">
                  <div className="time-slot">
                    <span className="slot-label">Loading...</span>
                    <span className="slot-amount">
                      <span className="spinner" />
                    </span>
                  </div>
                </div>
              </div>
              <div className="financial-card">
                <h3>Last 7 Days</h3>
                <div className="earnings-breakdown">
                  <div className="time-slot">
                    <span className="slot-label">Loading...</span>
                    <span className="slot-amount">
                      <span className="spinner" />
                    </span>
                  </div>
                </div>
              </div>
              <div className="financial-card">
                <h3>Last 30 Days</h3>
                <div className="earnings-breakdown">
                  <div className="time-slot">
                    <span className="slot-label">Loading...</span>
                    <span className="slot-amount">
                      <span className="spinner" />
                    </span>
                  </div>
                </div>
              </div>
              <div className="financial-card">
                <h3>Year to Date</h3>
                <div className="earnings-breakdown">
                  <div className="time-slot">
                    <span className="slot-label">Loading...</span>
                    <span className="slot-amount">
                      <span className="spinner" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="financial-grid">
              <div className="financial-card">
                <h3>Yesterday's Sales</h3>
                <div className="earnings-breakdown">
                  <div className="time-slot">
                    <span className="slot-label">Base Pay</span>
                    <span className="slot-amount expense">
                      {formatCurrency(financialData.yesterday.basePay)}
                    </span>
                  </div>
                  <div className="time-slot">
                    <span className="slot-label">Sales</span>
                    <span className="slot-amount sales">
                      {formatCurrency(financialData.yesterday.sales)}
                    </span>
                  </div>
                  <div className="time-slot total">
                    <span className="slot-label">Profit</span>
                    <span
                      className={`slot-amount ${
                        financialData.yesterday.profit >= 0
                          ? "profit-positive"
                          : "profit-negative"
                      }`}
                    >
                      {formatCurrency(financialData.yesterday.profit)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="financial-card">
                <h3>Last 7 Days</h3>
                <div className="earnings-breakdown">
                  <div className="time-slot">
                    <span className="slot-label">Base Pay</span>
                    <span className="slot-amount expense">
                      {formatCurrency(financialData.last7Days.basePay)}
                    </span>
                  </div>
                  <div className="time-slot">
                    <span className="slot-label">Sales</span>
                    <span className="slot-amount sales">
                      {formatCurrency(financialData.last7Days.sales)}
                    </span>
                  </div>
                  <div className="time-slot total">
                    <span className="slot-label">Profit</span>
                    <span
                      className={`slot-amount ${
                        financialData.last7Days.profit >= 0
                          ? "profit-positive"
                          : "profit-negative"
                      }`}
                    >
                      {formatCurrency(financialData.last7Days.profit)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="financial-card">
                <h3>Last 30 Days</h3>
                <div className="earnings-breakdown">
                  <div className="time-slot">
                    <span className="slot-label">Base Pay</span>
                    <span className="slot-amount expense">
                      {formatCurrency(financialData.last30Days.basePay)}
                    </span>
                  </div>
                  <div className="time-slot">
                    <span className="slot-label">Sales</span>
                    <span className="slot-amount sales">
                      {formatCurrency(financialData.last30Days.sales)}
                    </span>
                  </div>
                  <div className="time-slot total">
                    <span className="slot-label">Profit</span>
                    <span
                      className={`slot-amount ${
                        financialData.last30Days.profit >= 0
                          ? "profit-positive"
                          : "profit-negative"
                      }`}
                    >
                      {formatCurrency(financialData.last30Days.profit)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="financial-card">
                <h3>Year to Date</h3>
                <div className="earnings-breakdown">
                  <div className="time-slot">
                    <span className="slot-label">Base Pay</span>
                    <span className="slot-amount expense">
                      {formatCurrency(financialData.yearToDate.basePay)}
                    </span>
                  </div>
                  <div className="time-slot">
                    <span className="slot-label">Sales</span>
                    <span className="slot-amount sales">
                      {formatCurrency(financialData.yearToDate.sales)}
                    </span>
                  </div>
                  <div className="time-slot total">
                    <span className="slot-label">Profit</span>
                    <span
                      className={`slot-amount ${
                        financialData.yearToDate.profit >= 0
                          ? "profit-positive"
                          : "profit-negative"
                      }`}
                    >
                      {formatCurrency(financialData.yearToDate.profit)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <AddShowModal
        isOpen={isAddShowModalOpen}
        onClose={closeAddShowModal}
        selectedBar={selectedBar}
        artists={artists}
        artistsLoading={artistsLoading}
        refreshBarShows={refreshBarShows}
        defaultDate={addShowDefaults.date}
        venueTimeZone={selectedBar?.time_zone}
        defaultTimeSlotId={addShowDefaults.timeSlotId}
        defaultArtistId={addShowDefaults.artistId}
      />
      <ShowCardModal
        isOpen={isShowModalOpen}
        onClose={closeShowCardModal}
        showId={selectedShow?.show_id}
        fallbackShow={selectedShow}
      />
    </DashboardLayout>
  );
};

export default BarOwnerDashboard;
